import { __decorate, __param } from '../../../../../../../external/tslib/tslib.es6.js';
import { localize2WithPath, localizeWithPath } from '../../../../nls.js';
import { Action } from '../../../../base/common/actions.js';
import { firstOrDefault } from '../../../../base/common/arrays.js';
import { EditorResourceAccessor, DEFAULT_EDITOR_ASSOCIATION } from '../../../common/editor.js';
import { SideBySideEditorInput } from '../../../common/editor/sideBySideEditorInput.js';
import { IWorkbenchLayoutService } from '../../../services/layout/browser/layoutService.service.js';
import { IHistoryService } from '../../../services/history/common/history.service.js';
import { IKeybindingService } from '../../../../platform/keybinding/common/keybinding.service.js';
import { ICommandService } from '../../../../platform/commands/common/commands.service.js';
import { SPLIT_EDITOR, SPLIT_EDITOR_UP, SPLIT_EDITOR_DOWN, CLOSE_EDITOR_COMMAND_ID, UNPIN_EDITOR_COMMAND_ID, COPY_ACTIVE_EDITOR_COMMAND_ID, LAYOUT_EDITOR_GROUPS_COMMAND_ID, splitEditor, SPLIT_EDITOR_LEFT, SPLIT_EDITOR_RIGHT, TOGGLE_MAXIMIZE_EDITOR_GROUP, resolveCommandsContext, getCommandsContext, MOVE_ACTIVE_EDITOR_COMMAND_ID, MOVE_EDITOR_INTO_NEW_WINDOW_COMMAND_ID, COPY_EDITOR_INTO_NEW_WINDOW_COMMAND_ID, MOVE_EDITOR_GROUP_INTO_NEW_WINDOW_COMMAND_ID, COPY_EDITOR_GROUP_INTO_NEW_WINDOW_COMMAND_ID, NEW_EMPTY_EDITOR_WINDOW_COMMAND_ID } from './editorCommands.js';
import { preferredSideBySideGroupDirection } from '../../../services/editor/common/editorGroupsService.js';
import { IEditorGroupsService } from '../../../services/editor/common/editorGroupsService.service.js';
import { IEditorService } from '../../../services/editor/common/editorService.service.js';
import { IConfigurationService } from '../../../../platform/configuration/common/configuration.service.js';
import { IWorkspacesService } from '../../../../platform/workspaces/common/workspaces.service.js';
import { IDialogService, IFileDialogService } from '../../../../platform/dialogs/common/dialogs.service.js';
import { ItemActivation } from '../../../../platform/quickinput/common/quickInput.js';
import { IQuickInputService } from '../../../../platform/quickinput/common/quickInput.service.js';
import { ActiveGroupEditorsByMostRecentlyUsedQuickAccess, AllEditorsByAppearanceQuickAccess, AllEditorsByMostRecentlyUsedQuickAccess } from './editorQuickAccess.js';
import { Codicon } from '../../../../base/common/codicons.js';
import { ThemeIcon } from '../../../../base/common/themables.js';
import { IFilesConfigurationService } from '../../../services/filesConfiguration/common/filesConfigurationService.service.js';
import { IEditorResolverService } from '../../../services/editor/common/editorResolverService.service.js';
import { isNative, isWindows, isLinux } from '../../../../base/common/platform.js';
import { Action2, MenuId } from '../../../../platform/actions/common/actions.js';
import { ContextKeyExpr } from '../../../../platform/contextkey/common/contextkey.js';
import { KeyChord } from '../../../../base/common/keyCodes.js';
import { ILogService } from '../../../../platform/log/common/log.service.js';
import { Categories } from '../../../../platform/action/common/actionCommonCategories.js';
import { MultipleEditorGroupsContext, SideBarVisibleContext, AuxiliaryBarVisibleContext, EditorPartMaximizedEditorGroupContext, EditorPartMultipleEditorGroupsContext, ActiveEditorGroupEmptyContext, ActiveEditorAvailableEditorIdsContext, IsAuxiliaryWindowFocusedContext, ActiveEditorContext } from '../../../common/contextkeys.js';
import { getActiveDocument } from '../../../../base/browser/dom.js';
const _moduleId = "vs/workbench/browser/parts/editor/editorActions";
class ExecuteCommandAction extends Action2 {
    constructor(desc, commandId, commandArgs) {
        super(desc);
        this.commandId = commandId;
        this.commandArgs = commandArgs;
    }
    run(accessor) {
        const commandService = accessor.get(ICommandService);
        return commandService.executeCommand(this.commandId, this.commandArgs);
    }
}
class AbstractSplitEditorAction extends Action2 {
    getDirection(configurationService) {
        return preferredSideBySideGroupDirection(configurationService);
    }
    async run(accessor, context) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const configurationService = accessor.get(IConfigurationService);
        splitEditor(editorGroupService, this.getDirection(configurationService), context);
    }
}
class SplitEditorAction extends AbstractSplitEditorAction {
    static { this.ID = SPLIT_EDITOR; }
    constructor() {
        super({
            id: SplitEditorAction.ID,
            title: ( localize2WithPath(_moduleId, 0, 'Split Editor')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 93
            },
            category: Categories.View
        });
    }
}
class SplitEditorOrthogonalAction extends AbstractSplitEditorAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorOrthogonal',
            title: ( localize2WithPath(_moduleId, 1, 'Split Editor Orthogonal')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 93 )
            },
            category: Categories.View
        });
    }
    getDirection(configurationService) {
        const direction = preferredSideBySideGroupDirection(configurationService);
        return direction === 3  ? 1  : 3 ;
    }
}
class SplitEditorLeftAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: SPLIT_EDITOR_LEFT,
            title: ( localize2WithPath(_moduleId, 2, 'Split Editor Left')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 93 )
            },
            category: Categories.View
        }, SPLIT_EDITOR_LEFT);
    }
}
class SplitEditorRightAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: SPLIT_EDITOR_RIGHT,
            title: ( localize2WithPath(_moduleId, 3, 'Split Editor Right')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 93 )
            },
            category: Categories.View
        }, SPLIT_EDITOR_RIGHT);
    }
}
class SplitEditorUpAction extends ExecuteCommandAction {
    static { this.LABEL = ( localizeWithPath(_moduleId, 4, "Split Editor Up")); }
    constructor() {
        super({
            id: SPLIT_EDITOR_UP,
            title: ( localize2WithPath(_moduleId, 4, "Split Editor Up")),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 93 )
            },
            category: Categories.View
        }, SPLIT_EDITOR_UP);
    }
}
class SplitEditorDownAction extends ExecuteCommandAction {
    static { this.LABEL = ( localizeWithPath(_moduleId, 5, "Split Editor Down")); }
    constructor() {
        super({
            id: SPLIT_EDITOR_DOWN,
            title: ( localize2WithPath(_moduleId, 5, "Split Editor Down")),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 93 )
            },
            category: Categories.View
        }, SPLIT_EDITOR_DOWN);
    }
}
class JoinTwoGroupsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.joinTwoGroups',
            title: ( localize2WithPath(_moduleId, 6, 'Join Editor Group with Next Group')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor, context) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        let sourceGroup;
        if (context && typeof context.groupId === 'number') {
            sourceGroup = editorGroupService.getGroup(context.groupId);
        }
        else {
            sourceGroup = editorGroupService.activeGroup;
        }
        if (sourceGroup) {
            const targetGroupDirections = [3 , 1 , 2 , 0 ];
            for (const targetGroupDirection of targetGroupDirections) {
                const targetGroup = editorGroupService.findGroup({ direction: targetGroupDirection }, sourceGroup);
                if (targetGroup && sourceGroup !== targetGroup) {
                    editorGroupService.mergeGroup(sourceGroup, targetGroup);
                    break;
                }
            }
        }
    }
}
class JoinAllGroupsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.joinAllGroups',
            title: ( localize2WithPath(_moduleId, 7, 'Join All Editor Groups')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        editorGroupService.mergeAllGroups(editorGroupService.activeGroup);
    }
}
class NavigateBetweenGroupsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateEditorGroups',
            title: ( localize2WithPath(_moduleId, 8, 'Navigate Between Editor Groups')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const nextGroup = editorGroupService.findGroup({ location: 2  }, editorGroupService.activeGroup, true);
        nextGroup?.focus();
    }
}
class FocusActiveGroupAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.focusActiveEditorGroup',
            title: ( localize2WithPath(_moduleId, 9, 'Focus Active Editor Group')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        editorGroupService.activeGroup.focus();
    }
}
class AbstractFocusGroupAction extends Action2 {
    constructor(desc, scope) {
        super(desc);
        this.scope = scope;
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const group = editorGroupService.findGroup(this.scope, editorGroupService.activeGroup, true);
        group?.focus();
    }
}
class FocusFirstGroupAction extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusFirstEditorGroup',
            title: ( localize2WithPath(_moduleId, 10, 'Focus First Editor Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 22
            },
            category: Categories.View
        }, { location: 0  });
    }
}
class FocusLastGroupAction extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusLastEditorGroup',
            title: ( localize2WithPath(_moduleId, 11, 'Focus Last Editor Group')),
            f1: true,
            category: Categories.View
        }, { location: 1  });
    }
}
class FocusNextGroup extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusNextGroup',
            title: ( localize2WithPath(_moduleId, 12, 'Focus Next Editor Group')),
            f1: true,
            category: Categories.View
        }, { location: 2  });
    }
}
class FocusPreviousGroup extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusPreviousGroup',
            title: ( localize2WithPath(_moduleId, 13, 'Focus Previous Editor Group')),
            f1: true,
            category: Categories.View
        }, { location: 3  });
    }
}
class FocusLeftGroup extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusLeftGroup',
            title: ( localize2WithPath(_moduleId, 14, 'Focus Left Editor Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 15 )
            },
            category: Categories.View
        }, { direction: 2  });
    }
}
class FocusRightGroup extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusRightGroup',
            title: ( localize2WithPath(_moduleId, 15, 'Focus Right Editor Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 17 )
            },
            category: Categories.View
        }, { direction: 3  });
    }
}
class FocusAboveGroup extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusAboveGroup',
            title: ( localize2WithPath(_moduleId, 16, 'Focus Editor Group Above')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 16 )
            },
            category: Categories.View
        }, { direction: 0  });
    }
}
class FocusBelowGroup extends AbstractFocusGroupAction {
    constructor() {
        super({
            id: 'workbench.action.focusBelowGroup',
            title: ( localize2WithPath(_moduleId, 17, 'Focus Editor Group Below')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 18 )
            },
            category: Categories.View
        }, { direction: 1  });
    }
}
let CloseEditorAction = class CloseEditorAction extends Action {
    static { this.ID = 'workbench.action.closeActiveEditor'; }
    static { this.LABEL = ( localizeWithPath(_moduleId, 18, "Close Editor")); }
    constructor(id, label, commandService) {
        super(id, label, ThemeIcon.asClassName(Codicon.close));
        this.commandService = commandService;
    }
    run(context) {
        return this.commandService.executeCommand(CLOSE_EDITOR_COMMAND_ID, undefined, context);
    }
};
CloseEditorAction = ( (__decorate([
    ( (__param(2, ICommandService)))
], CloseEditorAction)));
let UnpinEditorAction = class UnpinEditorAction extends Action {
    static { this.ID = 'workbench.action.unpinActiveEditor'; }
    static { this.LABEL = ( localizeWithPath(_moduleId, 19, "Unpin Editor")); }
    constructor(id, label, commandService) {
        super(id, label, ThemeIcon.asClassName(Codicon.pinned));
        this.commandService = commandService;
    }
    run(context) {
        return this.commandService.executeCommand(UNPIN_EDITOR_COMMAND_ID, undefined, context);
    }
};
UnpinEditorAction = ( (__decorate([
    ( (__param(2, ICommandService)))
], UnpinEditorAction)));
let CloseOneEditorAction = class CloseOneEditorAction extends Action {
    static { this.ID = 'workbench.action.closeActiveEditor'; }
    static { this.LABEL = ( localizeWithPath(_moduleId, 20, "Close")); }
    constructor(id, label, editorGroupService) {
        super(id, label, ThemeIcon.asClassName(Codicon.close));
        this.editorGroupService = editorGroupService;
    }
    async run(context) {
        let group;
        let editorIndex;
        if (context) {
            group = this.editorGroupService.getGroup(context.groupId);
            if (group) {
                editorIndex = context.editorIndex;
            }
        }
        if (!group) {
            group = this.editorGroupService.activeGroup;
        }
        if (typeof editorIndex === 'number') {
            const editorAtIndex = group.getEditorByIndex(editorIndex);
            if (editorAtIndex) {
                await group.closeEditor(editorAtIndex, { preserveFocus: context?.preserveFocus });
                return;
            }
        }
        if (group.activeEditor) {
            await group.closeEditor(group.activeEditor, { preserveFocus: context?.preserveFocus });
            return;
        }
    }
};
CloseOneEditorAction = ( (__decorate([
    ( (__param(2, IEditorGroupsService)))
], CloseOneEditorAction)));
class RevertAndCloseEditorAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.revertAndCloseActiveEditor',
            title: ( localize2WithPath(_moduleId, 21, 'Revert and Close Editor')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const logService = accessor.get(ILogService);
        const activeEditorPane = editorService.activeEditorPane;
        if (activeEditorPane) {
            const editor = activeEditorPane.input;
            const group = activeEditorPane.group;
            try {
                await editorService.revert({ editor, groupId: group.id });
            }
            catch (error) {
                logService.error(error);
                await editorService.revert({ editor, groupId: group.id }, { soft: true });
            }
            await group.closeEditor(editor);
        }
    }
}
class CloseLeftEditorsInGroupAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.closeEditorsToTheLeft',
            title: ( localize2WithPath(_moduleId, 22, 'Close Editors to the Left in Group')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor, context) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const { group, editor } = this.getTarget(editorGroupService, context);
        if (group && editor) {
            await group.closeEditors({ direction: 0 , except: editor, excludeSticky: true });
        }
    }
    getTarget(editorGroupService, context) {
        if (context) {
            return { editor: context.editor, group: editorGroupService.getGroup(context.groupId) };
        }
        return { group: editorGroupService.activeGroup, editor: editorGroupService.activeGroup.activeEditor };
    }
}
class AbstractCloseAllAction extends Action2 {
    groupsToClose(editorGroupService) {
        const groupsToClose = [];
        const groups = editorGroupService.getGroups(2 );
        for (let i = groups.length - 1; i >= 0; i--) {
            groupsToClose.push(groups[i]);
        }
        return groupsToClose;
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const editorGroupService = accessor.get(IEditorGroupsService);
        const filesConfigurationService = accessor.get(IFilesConfigurationService);
        const fileDialogService = accessor.get(IFileDialogService);
        const dirtyEditorsWithDefaultConfirm = ( (new Set()));
        const dirtyAutoSaveOnFocusChangeEditors = ( (new Set()));
        const dirtyAutoSaveOnWindowChangeEditors = ( (new Set()));
        const editorsWithCustomConfirm = ( (new Map()));
        for (const { editor, groupId } of editorService.getEditors(1 , { excludeSticky: this.excludeSticky })) {
            let confirmClose = false;
            if (editor.closeHandler) {
                confirmClose = editor.closeHandler.showConfirm();
            }
            else {
                confirmClose = editor.isDirty() && !editor.isSaving();
            }
            if (!confirmClose) {
                continue;
            }
            if (typeof editor.closeHandler?.confirm === 'function') {
                let customEditorsToConfirm = editorsWithCustomConfirm.get(editor.typeId);
                if (!customEditorsToConfirm) {
                    customEditorsToConfirm = ( (new Set()));
                    editorsWithCustomConfirm.set(editor.typeId, customEditorsToConfirm);
                }
                customEditorsToConfirm.add({ editor, groupId });
            }
            else if (!editor.hasCapability(4 ) && filesConfigurationService.getAutoSaveMode(editor).mode === 3 ) {
                dirtyAutoSaveOnFocusChangeEditors.add({ editor, groupId });
            }
            else if ((isNative && (isWindows || isLinux)) && !editor.hasCapability(4 ) && filesConfigurationService.getAutoSaveMode(editor).mode === 4 ) {
                dirtyAutoSaveOnWindowChangeEditors.add({ editor, groupId });
            }
            else {
                dirtyEditorsWithDefaultConfirm.add({ editor, groupId });
            }
        }
        if (dirtyEditorsWithDefaultConfirm.size > 0) {
            const editors = Array.from(( (dirtyEditorsWithDefaultConfirm.values())));
            await this.revealEditorsToConfirm(editors, editorGroupService);
            const confirmation = await fileDialogService.showSaveConfirm(( (editors.map(({ editor }) => {
                if (editor instanceof SideBySideEditorInput) {
                    return editor.primary.getName();
                }
                return editor.getName();
            }))));
            switch (confirmation) {
                case 2 :
                    return;
                case 1 :
                    await editorService.revert(editors, { soft: true });
                    break;
                case 0 :
                    await editorService.save(editors, { reason: 1  });
                    break;
            }
        }
        for (const [, editorIdentifiers] of editorsWithCustomConfirm) {
            const editors = Array.from(( (editorIdentifiers.values())));
            await this.revealEditorsToConfirm(editors, editorGroupService);
            const confirmation = await firstOrDefault(editors)?.editor.closeHandler?.confirm?.(editors);
            if (typeof confirmation === 'number') {
                switch (confirmation) {
                    case 2 :
                        return;
                    case 1 :
                        await editorService.revert(editors, { soft: true });
                        break;
                    case 0 :
                        await editorService.save(editors, { reason: 1  });
                        break;
                }
            }
        }
        if (dirtyAutoSaveOnFocusChangeEditors.size > 0) {
            const editors = Array.from(( (dirtyAutoSaveOnFocusChangeEditors.values())));
            await editorService.save(editors, { reason: 3  });
        }
        if (dirtyAutoSaveOnWindowChangeEditors.size > 0) {
            const editors = Array.from(( (dirtyAutoSaveOnWindowChangeEditors.values())));
            await editorService.save(editors, { reason: 4  });
        }
        return this.doCloseAll(editorGroupService);
    }
    async revealEditorsToConfirm(editors, editorGroupService) {
        try {
            const handledGroups = ( (new Set()));
            for (const { editor, groupId } of editors) {
                if (( (handledGroups.has(groupId)))) {
                    continue;
                }
                handledGroups.add(groupId);
                const group = editorGroupService.getGroup(groupId);
                await group?.openEditor(editor);
            }
        }
        catch (error) {
        }
    }
    async doCloseAll(editorGroupService) {
        await Promise.all(( (this.groupsToClose(editorGroupService).map(group => group.closeAllEditors({ excludeSticky: this.excludeSticky })))));
    }
}
class CloseAllEditorsAction extends AbstractCloseAllAction {
    static { this.ID = 'workbench.action.closeAllEditors'; }
    static { this.LABEL = ( localize2WithPath(_moduleId, 23, 'Close All Editors')); }
    constructor() {
        super({
            id: CloseAllEditorsAction.ID,
            title: CloseAllEditorsAction.LABEL,
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 53 )
            },
            icon: Codicon.closeAll,
            category: Categories.View
        });
    }
    get excludeSticky() {
        return true;
    }
}
class CloseAllEditorGroupsAction extends AbstractCloseAllAction {
    constructor() {
        super({
            id: 'workbench.action.closeAllGroups',
            title: ( localize2WithPath(_moduleId, 24, 'Close All Editor Groups')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 1024  | 53 )
            },
            category: Categories.View
        });
    }
    get excludeSticky() {
        return false;
    }
    async doCloseAll(editorGroupService) {
        await super.doCloseAll(editorGroupService);
        for (const groupToClose of this.groupsToClose(editorGroupService)) {
            editorGroupService.removeGroup(groupToClose);
        }
    }
}
class CloseEditorsInOtherGroupsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.closeEditorsInOtherGroups',
            title: ( localize2WithPath(_moduleId, 25, 'Close Editors in Other Groups')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor, context) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const groupToSkip = context ? editorGroupService.getGroup(context.groupId) : editorGroupService.activeGroup;
        await Promise.all(( (editorGroupService.getGroups(1 ).map(async (group) => {
            if (groupToSkip && group.id === groupToSkip.id) {
                return;
            }
            return group.closeAllEditors({ excludeSticky: true });
        }))));
    }
}
class CloseEditorInAllGroupsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.closeEditorInAllGroups',
            title: ( localize2WithPath(_moduleId, 26, 'Close Editor in All Groups')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const editorGroupService = accessor.get(IEditorGroupsService);
        const activeEditor = editorService.activeEditor;
        if (activeEditor) {
            await Promise.all(( (editorGroupService.getGroups(1 ).map(group => group.closeEditor(activeEditor)))));
        }
    }
}
class AbstractMoveCopyGroupAction extends Action2 {
    constructor(desc, direction, isMove) {
        super(desc);
        this.direction = direction;
        this.isMove = isMove;
    }
    async run(accessor, context) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        let sourceGroup;
        if (context && typeof context.groupId === 'number') {
            sourceGroup = editorGroupService.getGroup(context.groupId);
        }
        else {
            sourceGroup = editorGroupService.activeGroup;
        }
        if (sourceGroup) {
            let resultGroup = undefined;
            if (this.isMove) {
                const targetGroup = this.findTargetGroup(editorGroupService, sourceGroup);
                if (targetGroup) {
                    resultGroup = editorGroupService.moveGroup(sourceGroup, targetGroup, this.direction);
                }
            }
            else {
                resultGroup = editorGroupService.copyGroup(sourceGroup, sourceGroup, this.direction);
            }
            if (resultGroup) {
                editorGroupService.activateGroup(resultGroup);
            }
        }
    }
    findTargetGroup(editorGroupService, sourceGroup) {
        const targetNeighbours = [this.direction];
        switch (this.direction) {
            case 2 :
            case 3 :
                targetNeighbours.push(0 , 1 );
                break;
            case 0 :
            case 1 :
                targetNeighbours.push(2 , 3 );
                break;
        }
        for (const targetNeighbour of targetNeighbours) {
            const targetNeighbourGroup = editorGroupService.findGroup({ direction: targetNeighbour }, sourceGroup);
            if (targetNeighbourGroup) {
                return targetNeighbourGroup;
            }
        }
        return undefined;
    }
}
class AbstractMoveGroupAction extends AbstractMoveCopyGroupAction {
    constructor(desc, direction) {
        super(desc, direction, true);
    }
}
class MoveGroupLeftAction extends AbstractMoveGroupAction {
    constructor() {
        super({
            id: 'workbench.action.moveActiveEditorGroupLeft',
            title: ( localize2WithPath(_moduleId, 27, 'Move Editor Group Left')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 15 )
            },
            category: Categories.View
        }, 2 );
    }
}
class MoveGroupRightAction extends AbstractMoveGroupAction {
    constructor() {
        super({
            id: 'workbench.action.moveActiveEditorGroupRight',
            title: ( localize2WithPath(_moduleId, 28, 'Move Editor Group Right')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 17 )
            },
            category: Categories.View
        }, 3 );
    }
}
class MoveGroupUpAction extends AbstractMoveGroupAction {
    constructor() {
        super({
            id: 'workbench.action.moveActiveEditorGroupUp',
            title: ( localize2WithPath(_moduleId, 29, 'Move Editor Group Up')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 16 )
            },
            category: Categories.View
        }, 0 );
    }
}
class MoveGroupDownAction extends AbstractMoveGroupAction {
    constructor() {
        super({
            id: 'workbench.action.moveActiveEditorGroupDown',
            title: ( localize2WithPath(_moduleId, 30, 'Move Editor Group Down')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 18 )
            },
            category: Categories.View
        }, 1 );
    }
}
class AbstractDuplicateGroupAction extends AbstractMoveCopyGroupAction {
    constructor(desc, direction) {
        super(desc, direction, false);
    }
}
class DuplicateGroupLeftAction extends AbstractDuplicateGroupAction {
    constructor() {
        super({
            id: 'workbench.action.duplicateActiveEditorGroupLeft',
            title: ( localize2WithPath(_moduleId, 31, 'Duplicate Editor Group Left')),
            f1: true,
            category: Categories.View
        }, 2 );
    }
}
class DuplicateGroupRightAction extends AbstractDuplicateGroupAction {
    constructor() {
        super({
            id: 'workbench.action.duplicateActiveEditorGroupRight',
            title: ( localize2WithPath(_moduleId, 32, 'Duplicate Editor Group Right')),
            f1: true,
            category: Categories.View
        }, 3 );
    }
}
class DuplicateGroupUpAction extends AbstractDuplicateGroupAction {
    constructor() {
        super({
            id: 'workbench.action.duplicateActiveEditorGroupUp',
            title: ( localize2WithPath(_moduleId, 33, 'Duplicate Editor Group Up')),
            f1: true,
            category: Categories.View
        }, 0 );
    }
}
class DuplicateGroupDownAction extends AbstractDuplicateGroupAction {
    constructor() {
        super({
            id: 'workbench.action.duplicateActiveEditorGroupDown',
            title: ( localize2WithPath(_moduleId, 34, 'Duplicate Editor Group Down')),
            f1: true,
            category: Categories.View
        }, 1 );
    }
}
class MinimizeOtherGroupsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.minimizeOtherEditors',
            title: ( localize2WithPath(_moduleId, 35, 'Expand Editor Group')),
            f1: true,
            category: Categories.View,
            precondition: MultipleEditorGroupsContext
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        editorGroupService.arrangeGroups(1 );
    }
}
class MinimizeOtherGroupsHideSidebarAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.minimizeOtherEditorsHideSidebar',
            title: ( localize2WithPath(_moduleId, 36, 'Expand Editor Group and Hide Side Bars')),
            f1: true,
            category: Categories.View,
            precondition: ( (ContextKeyExpr.or(
                MultipleEditorGroupsContext,
                SideBarVisibleContext,
                AuxiliaryBarVisibleContext
            )))
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const layoutService = accessor.get(IWorkbenchLayoutService);
        layoutService.setPartHidden(true, "workbench.parts.sidebar" );
        layoutService.setPartHidden(true, "workbench.parts.auxiliarybar" );
        editorGroupService.arrangeGroups(1 );
    }
}
class ResetGroupSizesAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.evenEditorWidths',
            title: ( localize2WithPath(_moduleId, 37, 'Reset Editor Group Sizes')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        editorGroupService.arrangeGroups(2 );
    }
}
class ToggleGroupSizesAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.toggleEditorWidths',
            title: ( localize2WithPath(_moduleId, 38, 'Toggle Editor Group Sizes')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        editorGroupService.toggleExpandGroup();
    }
}
class MaximizeGroupHideSidebarAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.maximizeEditorHideSidebar',
            title: ( localize2WithPath(_moduleId, 39, 'Maximize Editor Group and Hide Side Bars')),
            f1: true,
            category: Categories.View,
            precondition: ( (ContextKeyExpr.or( (ContextKeyExpr.and(
                 (EditorPartMaximizedEditorGroupContext.negate()),
                EditorPartMultipleEditorGroupsContext
            )), SideBarVisibleContext, AuxiliaryBarVisibleContext)))
        });
    }
    async run(accessor) {
        const layoutService = accessor.get(IWorkbenchLayoutService);
        const editorGroupService = accessor.get(IEditorGroupsService);
        const editorService = accessor.get(IEditorService);
        if (editorService.activeEditor) {
            layoutService.setPartHidden(true, "workbench.parts.sidebar" );
            layoutService.setPartHidden(true, "workbench.parts.auxiliarybar" );
            editorGroupService.arrangeGroups(0 );
        }
    }
}
class ToggleMaximizeEditorGroupAction extends Action2 {
    constructor() {
        super({
            id: TOGGLE_MAXIMIZE_EDITOR_GROUP,
            title: ( localize2WithPath(_moduleId, 40, 'Toggle Maximize Editor Group')),
            f1: true,
            category: Categories.View,
            precondition: ( (ContextKeyExpr.or(
                EditorPartMultipleEditorGroupsContext,
                EditorPartMaximizedEditorGroupContext
            ))),
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 43 ),
            },
            menu: [{
                    id: MenuId.EditorTitle,
                    order: -10000,
                    group: 'navigation',
                    when: EditorPartMaximizedEditorGroupContext
                },
                {
                    id: MenuId.EmptyEditorGroup,
                    order: -10000,
                    group: 'navigation',
                    when: EditorPartMaximizedEditorGroupContext
                }],
            icon: Codicon.screenFull,
            toggled: EditorPartMaximizedEditorGroupContext,
        });
    }
    async run(accessor, resourceOrContext, context) {
        const editorGroupsService = accessor.get(IEditorGroupsService);
        const { group } = resolveCommandsContext(editorGroupsService, getCommandsContext(resourceOrContext, context));
        editorGroupsService.toggleMaximizeGroup(group);
    }
}
class AbstractNavigateEditorAction extends Action2 {
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const result = this.navigate(editorGroupService);
        if (!result) {
            return;
        }
        const { groupId, editor } = result;
        if (!editor) {
            return;
        }
        const group = editorGroupService.getGroup(groupId);
        if (group) {
            await group.openEditor(editor);
        }
    }
}
class OpenNextEditor extends AbstractNavigateEditorAction {
    constructor() {
        super({
            id: 'workbench.action.nextEditor',
            title: ( localize2WithPath(_moduleId, 41, 'Open Next Editor')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 12 ,
                mac: {
                    primary: 2048  | 512  | 17 ,
                    secondary: [2048  | 1024  | 94 ]
                }
            },
            category: Categories.View
        });
    }
    navigate(editorGroupService) {
        const activeGroup = editorGroupService.activeGroup;
        const activeGroupEditors = activeGroup.getEditors(1 );
        const activeEditorIndex = activeGroup.activeEditor ? activeGroupEditors.indexOf(activeGroup.activeEditor) : -1;
        if (activeEditorIndex + 1 < activeGroupEditors.length) {
            return { editor: activeGroupEditors[activeEditorIndex + 1], groupId: activeGroup.id };
        }
        const handledGroups = ( (new Set()));
        let currentGroup = editorGroupService.activeGroup;
        while (currentGroup && !( (handledGroups.has(currentGroup.id)))) {
            currentGroup = editorGroupService.findGroup({ location: 2  }, currentGroup, true);
            if (currentGroup) {
                handledGroups.add(currentGroup.id);
                const groupEditors = currentGroup.getEditors(1 );
                if (groupEditors.length > 0) {
                    return { editor: groupEditors[0], groupId: currentGroup.id };
                }
            }
        }
        return undefined;
    }
}
class OpenPreviousEditor extends AbstractNavigateEditorAction {
    constructor() {
        super({
            id: 'workbench.action.previousEditor',
            title: ( localize2WithPath(_moduleId, 42, 'Open Previous Editor')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 11 ,
                mac: {
                    primary: 2048  | 512  | 15 ,
                    secondary: [2048  | 1024  | 92 ]
                }
            },
            category: Categories.View
        });
    }
    navigate(editorGroupService) {
        const activeGroup = editorGroupService.activeGroup;
        const activeGroupEditors = activeGroup.getEditors(1 );
        const activeEditorIndex = activeGroup.activeEditor ? activeGroupEditors.indexOf(activeGroup.activeEditor) : -1;
        if (activeEditorIndex > 0) {
            return { editor: activeGroupEditors[activeEditorIndex - 1], groupId: activeGroup.id };
        }
        const handledGroups = ( (new Set()));
        let currentGroup = editorGroupService.activeGroup;
        while (currentGroup && !( (handledGroups.has(currentGroup.id)))) {
            currentGroup = editorGroupService.findGroup({ location: 3  }, currentGroup, true);
            if (currentGroup) {
                handledGroups.add(currentGroup.id);
                const groupEditors = currentGroup.getEditors(1 );
                if (groupEditors.length > 0) {
                    return { editor: groupEditors[groupEditors.length - 1], groupId: currentGroup.id };
                }
            }
        }
        return undefined;
    }
}
class OpenNextEditorInGroup extends AbstractNavigateEditorAction {
    constructor() {
        super({
            id: 'workbench.action.nextEditorInGroup',
            title: ( localize2WithPath(_moduleId, 43, 'Open Next Editor in Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 12 ),
                mac: {
                    primary: KeyChord(2048  | 41 , 2048  | 512  | 17 )
                }
            },
            category: Categories.View
        });
    }
    navigate(editorGroupService) {
        const group = editorGroupService.activeGroup;
        const editors = group.getEditors(1 );
        const index = group.activeEditor ? editors.indexOf(group.activeEditor) : -1;
        return { editor: index + 1 < editors.length ? editors[index + 1] : editors[0], groupId: group.id };
    }
}
class OpenPreviousEditorInGroup extends AbstractNavigateEditorAction {
    constructor() {
        super({
            id: 'workbench.action.previousEditorInGroup',
            title: ( localize2WithPath(_moduleId, 44, 'Open Previous Editor in Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 11 ),
                mac: {
                    primary: KeyChord(2048  | 41 , 2048  | 512  | 15 )
                }
            },
            category: Categories.View
        });
    }
    navigate(editorGroupService) {
        const group = editorGroupService.activeGroup;
        const editors = group.getEditors(1 );
        const index = group.activeEditor ? editors.indexOf(group.activeEditor) : -1;
        return { editor: index > 0 ? editors[index - 1] : editors[editors.length - 1], groupId: group.id };
    }
}
class OpenFirstEditorInGroup extends AbstractNavigateEditorAction {
    constructor() {
        super({
            id: 'workbench.action.firstEditorInGroup',
            title: ( localize2WithPath(_moduleId, 45, 'Open First Editor in Group')),
            f1: true,
            category: Categories.View
        });
    }
    navigate(editorGroupService) {
        const group = editorGroupService.activeGroup;
        const editors = group.getEditors(1 );
        return { editor: editors[0], groupId: group.id };
    }
}
class OpenLastEditorInGroup extends AbstractNavigateEditorAction {
    constructor() {
        super({
            id: 'workbench.action.lastEditorInGroup',
            title: ( localize2WithPath(_moduleId, 46, 'Open Last Editor in Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 512  | 21 ,
                secondary: [2048  | 30 ],
                mac: {
                    primary: 256  | 21 ,
                    secondary: [2048  | 30 ]
                }
            },
            category: Categories.View
        });
    }
    navigate(editorGroupService) {
        const group = editorGroupService.activeGroup;
        const editors = group.getEditors(1 );
        return { editor: editors[editors.length - 1], groupId: group.id };
    }
}
class NavigateForwardAction extends Action2 {
    static { this.ID = 'workbench.action.navigateForward'; }
    static { this.LABEL = ( localizeWithPath(_moduleId, 47, "Go Forward")); }
    constructor() {
        super({
            id: NavigateForwardAction.ID,
            title: {
                ...( localize2WithPath(_moduleId, 47, "Go Forward")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 48, "&&Forward"))
            },
            f1: true,
            icon: Codicon.arrowRight,
            precondition: ( (ContextKeyExpr.has('canNavigateForward'))),
            keybinding: {
                weight: 200 ,
                win: { primary: 512  | 17  },
                mac: { primary: 256  | 1024  | 88  },
                linux: { primary: 2048  | 1024  | 88  }
            },
            menu: [
                { id: MenuId.MenubarGoMenu, group: '1_history_nav', order: 2 },
                { id: MenuId.CommandCenter, order: 2 }
            ]
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goForward(0 );
    }
}
class NavigateBackwardsAction extends Action2 {
    static { this.ID = 'workbench.action.navigateBack'; }
    static { this.LABEL = ( localizeWithPath(_moduleId, 49, "Go Back")); }
    constructor() {
        super({
            id: NavigateBackwardsAction.ID,
            title: {
                ...( localize2WithPath(_moduleId, 49, "Go Back")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 50, "&&Back"))
            },
            f1: true,
            precondition: ( (ContextKeyExpr.has('canNavigateBack'))),
            icon: Codicon.arrowLeft,
            keybinding: {
                weight: 200 ,
                win: { primary: 512  | 15  },
                mac: { primary: 256  | 88  },
                linux: { primary: 2048  | 512  | 88  }
            },
            menu: [
                { id: MenuId.MenubarGoMenu, group: '1_history_nav', order: 1 },
                { id: MenuId.CommandCenter, order: 1 }
            ]
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goBack(0 );
    }
}
class NavigatePreviousAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateLast',
            title: ( localize2WithPath(_moduleId, 51, 'Go Previous')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goPrevious(0 );
    }
}
class NavigateForwardInEditsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateForwardInEditLocations',
            title: ( localize2WithPath(_moduleId, 52, 'Go Forward in Edit Locations')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goForward(1 );
    }
}
class NavigateBackwardsInEditsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateBackInEditLocations',
            title: ( localize2WithPath(_moduleId, 53, 'Go Back in Edit Locations')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goBack(1 );
    }
}
class NavigatePreviousInEditsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigatePreviousInEditLocations',
            title: ( localize2WithPath(_moduleId, 54, 'Go Previous in Edit Locations')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goPrevious(1 );
    }
}
class NavigateToLastEditLocationAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateToLastEditLocation',
            title: ( localize2WithPath(_moduleId, 55, 'Go to Last Edit Location')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 47 )
            }
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goLast(1 );
    }
}
class NavigateForwardInNavigationsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateForwardInNavigationLocations',
            title: ( localize2WithPath(_moduleId, 56, 'Go Forward in Navigation Locations')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goForward(2 );
    }
}
class NavigateBackwardsInNavigationsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateBackInNavigationLocations',
            title: ( localize2WithPath(_moduleId, 57, 'Go Back in Navigation Locations')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goBack(2 );
    }
}
class NavigatePreviousInNavigationsAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigatePreviousInNavigationLocations',
            title: ( localize2WithPath(_moduleId, 58, 'Go Previous in Navigation Locations')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goPrevious(2 );
    }
}
class NavigateToLastNavigationLocationAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.navigateToLastNavigationLocation',
            title: ( localize2WithPath(_moduleId, 59, 'Go to Last Navigation Location')),
            f1: true
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.goLast(2 );
    }
}
class ReopenClosedEditorAction extends Action2 {
    static { this.ID = 'workbench.action.reopenClosedEditor'; }
    constructor() {
        super({
            id: ReopenClosedEditorAction.ID,
            title: ( localize2WithPath(_moduleId, 60, 'Reopen Closed Editor')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 1024  | 50
            },
            category: Categories.View
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        await historyService.reopenLastClosedEditor();
    }
}
class ClearRecentFilesAction extends Action2 {
    static { this.ID = 'workbench.action.clearRecentFiles'; }
    constructor() {
        super({
            id: ClearRecentFilesAction.ID,
            title: ( localize2WithPath(_moduleId, 61, 'Clear Recently Opened...')),
            f1: true,
            category: Categories.File
        });
    }
    async run(accessor) {
        const dialogService = accessor.get(IDialogService);
        const workspacesService = accessor.get(IWorkspacesService);
        const historyService = accessor.get(IHistoryService);
        const { confirmed } = await dialogService.confirm({
            type: 'warning',
            message: ( localizeWithPath(
                _moduleId,
                62,
                "Do you want to clear all recently opened files and workspaces?"
            )),
            detail: ( localizeWithPath(_moduleId, 63, "This action is irreversible!")),
            primaryButton: ( localizeWithPath(_moduleId, 64, "&&Clear"))
        });
        if (!confirmed) {
            return;
        }
        workspacesService.clearRecentlyOpened();
        historyService.clearRecentlyOpened();
    }
}
class ShowEditorsInActiveGroupByMostRecentlyUsedAction extends Action2 {
    static { this.ID = 'workbench.action.showEditorsInActiveGroup'; }
    constructor() {
        super({
            id: ShowEditorsInActiveGroupByMostRecentlyUsedAction.ID,
            title: ( localize2WithPath(_moduleId, 65, 'Show Editors in Active Group By Most Recently Used')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const quickInputService = accessor.get(IQuickInputService);
        quickInputService.quickAccess.show(ActiveGroupEditorsByMostRecentlyUsedQuickAccess.PREFIX);
    }
}
class ShowAllEditorsByAppearanceAction extends Action2 {
    static { this.ID = 'workbench.action.showAllEditors'; }
    constructor() {
        super({
            id: ShowAllEditorsByAppearanceAction.ID,
            title: ( localize2WithPath(_moduleId, 66, 'Show All Editors By Appearance')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: KeyChord(2048  | 41 , 2048  | 46 ),
                mac: {
                    primary: 2048  | 512  | 2
                }
            },
            category: Categories.File
        });
    }
    async run(accessor) {
        const quickInputService = accessor.get(IQuickInputService);
        quickInputService.quickAccess.show(AllEditorsByAppearanceQuickAccess.PREFIX);
    }
}
class ShowAllEditorsByMostRecentlyUsedAction extends Action2 {
    static { this.ID = 'workbench.action.showAllEditorsByMostRecentlyUsed'; }
    constructor() {
        super({
            id: ShowAllEditorsByMostRecentlyUsedAction.ID,
            title: ( localize2WithPath(_moduleId, 67, 'Show All Editors By Most Recently Used')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const quickInputService = accessor.get(IQuickInputService);
        quickInputService.quickAccess.show(AllEditorsByMostRecentlyUsedQuickAccess.PREFIX);
    }
}
class AbstractQuickAccessEditorAction extends Action2 {
    constructor(desc, prefix, itemActivation) {
        super(desc);
        this.prefix = prefix;
        this.itemActivation = itemActivation;
    }
    async run(accessor) {
        const keybindingService = accessor.get(IKeybindingService);
        const quickInputService = accessor.get(IQuickInputService);
        const keybindings = keybindingService.lookupKeybindings(this.desc.id);
        quickInputService.quickAccess.show(this.prefix, {
            quickNavigateConfiguration: { keybindings },
            itemActivation: this.itemActivation
        });
    }
}
class QuickAccessPreviousRecentlyUsedEditorAction extends AbstractQuickAccessEditorAction {
    constructor() {
        super({
            id: 'workbench.action.quickOpenPreviousRecentlyUsedEditor',
            title: ( localize2WithPath(_moduleId, 68, 'Quick Open Previous Recently Used Editor')),
            f1: true,
            category: Categories.View
        }, AllEditorsByMostRecentlyUsedQuickAccess.PREFIX, undefined);
    }
}
class QuickAccessLeastRecentlyUsedEditorAction extends AbstractQuickAccessEditorAction {
    constructor() {
        super({
            id: 'workbench.action.quickOpenLeastRecentlyUsedEditor',
            title: ( localize2WithPath(_moduleId, 69, 'Quick Open Least Recently Used Editor')),
            f1: true,
            category: Categories.View
        }, AllEditorsByMostRecentlyUsedQuickAccess.PREFIX, undefined);
    }
}
class QuickAccessPreviousRecentlyUsedEditorInGroupAction extends AbstractQuickAccessEditorAction {
    constructor() {
        super({
            id: 'workbench.action.quickOpenPreviousRecentlyUsedEditorInGroup',
            title: ( localize2WithPath(_moduleId, 70, 'Quick Open Previous Recently Used Editor in Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 2 ,
                mac: {
                    primary: 256  | 2
                }
            },
            precondition: ( (ActiveEditorGroupEmptyContext.toNegated())),
            category: Categories.View
        }, ActiveGroupEditorsByMostRecentlyUsedQuickAccess.PREFIX, undefined);
    }
}
class QuickAccessLeastRecentlyUsedEditorInGroupAction extends AbstractQuickAccessEditorAction {
    constructor() {
        super({
            id: 'workbench.action.quickOpenLeastRecentlyUsedEditorInGroup',
            title: ( localize2WithPath(_moduleId, 71, 'Quick Open Least Recently Used Editor in Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 1024  | 2 ,
                mac: {
                    primary: 256  | 1024  | 2
                }
            },
            precondition: ( (ActiveEditorGroupEmptyContext.toNegated())),
            category: Categories.View
        }, ActiveGroupEditorsByMostRecentlyUsedQuickAccess.PREFIX, ItemActivation.LAST);
    }
}
class QuickAccessPreviousEditorFromHistoryAction extends Action2 {
    static { this.ID = 'workbench.action.openPreviousEditorFromHistory'; }
    constructor() {
        super({
            id: QuickAccessPreviousEditorFromHistoryAction.ID,
            title: ( localize2WithPath(_moduleId, 72, 'Quick Open Previous Editor from History')),
            f1: true
        });
    }
    async run(accessor) {
        const keybindingService = accessor.get(IKeybindingService);
        const quickInputService = accessor.get(IQuickInputService);
        const editorGroupService = accessor.get(IEditorGroupsService);
        const keybindings = keybindingService.lookupKeybindings(QuickAccessPreviousEditorFromHistoryAction.ID);
        let itemActivation = undefined;
        if (editorGroupService.activeGroup.count === 0) {
            itemActivation = ItemActivation.FIRST;
        }
        quickInputService.quickAccess.show('', { quickNavigateConfiguration: { keybindings }, itemActivation });
    }
}
class OpenNextRecentlyUsedEditorAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.openNextRecentlyUsedEditor',
            title: ( localize2WithPath(_moduleId, 73, 'Open Next Recently Used Editor')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        historyService.openNextRecentlyUsedEditor();
    }
}
class OpenPreviousRecentlyUsedEditorAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.openPreviousRecentlyUsedEditor',
            title: ( localize2WithPath(_moduleId, 74, 'Open Previous Recently Used Editor')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        historyService.openPreviouslyUsedEditor();
    }
}
class OpenNextRecentlyUsedEditorInGroupAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.openNextRecentlyUsedEditorInGroup',
            title: ( localize2WithPath(_moduleId, 75, 'Open Next Recently Used Editor In Group')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        const editorGroupsService = accessor.get(IEditorGroupsService);
        historyService.openNextRecentlyUsedEditor(editorGroupsService.activeGroup.id);
    }
}
class OpenPreviousRecentlyUsedEditorInGroupAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.openPreviousRecentlyUsedEditorInGroup',
            title: ( localize2WithPath(_moduleId, 76, 'Open Previous Recently Used Editor In Group')),
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const historyService = accessor.get(IHistoryService);
        const editorGroupsService = accessor.get(IEditorGroupsService);
        historyService.openPreviouslyUsedEditor(editorGroupsService.activeGroup.id);
    }
}
class ClearEditorHistoryAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.clearEditorHistory',
            title: ( localize2WithPath(_moduleId, 77, 'Clear Editor History')),
            f1: true
        });
    }
    async run(accessor) {
        const dialogService = accessor.get(IDialogService);
        const historyService = accessor.get(IHistoryService);
        const { confirmed } = await dialogService.confirm({
            type: 'warning',
            message: ( localizeWithPath(
                _moduleId,
                78,
                "Do you want to clear the history of recently opened editors?"
            )),
            detail: ( localizeWithPath(_moduleId, 63, "This action is irreversible!")),
            primaryButton: ( localizeWithPath(_moduleId, 64, "&&Clear"))
        });
        if (!confirmed) {
            return;
        }
        historyService.clear();
    }
}
class MoveEditorLeftInGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorLeftInGroup',
            title: ( localize2WithPath(_moduleId, 79, 'Move Editor Left')),
            keybinding: {
                weight: 200 ,
                primary: 2048  | 1024  | 11 ,
                mac: {
                    primary: KeyChord(2048  | 41 , 2048  | 1024  | 15 )
                }
            },
            f1: true,
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'left' });
    }
}
class MoveEditorRightInGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorRightInGroup',
            title: ( localize2WithPath(_moduleId, 80, 'Move Editor Right')),
            keybinding: {
                weight: 200 ,
                primary: 2048  | 1024  | 12 ,
                mac: {
                    primary: KeyChord(2048  | 41 , 2048  | 1024  | 17 )
                }
            },
            f1: true,
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'right' });
    }
}
class MoveEditorToPreviousGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToPreviousGroup',
            title: ( localize2WithPath(_moduleId, 81, 'Move Editor into Previous Group')),
            keybinding: {
                weight: 200 ,
                primary: 2048  | 512  | 15 ,
                mac: {
                    primary: 2048  | 256  | 15
                }
            },
            f1: true,
            category: Categories.View,
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'previous', by: 'group' });
    }
}
class MoveEditorToNextGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToNextGroup',
            title: ( localize2WithPath(_moduleId, 82, 'Move Editor into Next Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 2048  | 512  | 17 ,
                mac: {
                    primary: 2048  | 256  | 17
                }
            },
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'next', by: 'group' });
    }
}
class MoveEditorToAboveGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToAboveGroup',
            title: ( localize2WithPath(_moduleId, 83, 'Move Editor into Group Above')),
            f1: true,
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'up', by: 'group' });
    }
}
class MoveEditorToBelowGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToBelowGroup',
            title: ( localize2WithPath(_moduleId, 84, 'Move Editor into Group Below')),
            f1: true,
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'down', by: 'group' });
    }
}
class MoveEditorToLeftGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToLeftGroup',
            title: ( localize2WithPath(_moduleId, 85, 'Move Editor into Left Group')),
            f1: true,
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'left', by: 'group' });
    }
}
class MoveEditorToRightGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToRightGroup',
            title: ( localize2WithPath(_moduleId, 86, 'Move Editor into Right Group')),
            f1: true,
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'right', by: 'group' });
    }
}
class MoveEditorToFirstGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToFirstGroup',
            title: ( localize2WithPath(_moduleId, 87, 'Move Editor into First Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 1024  | 512  | 22 ,
                mac: {
                    primary: 2048  | 256  | 22
                }
            },
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'first', by: 'group' });
    }
}
class MoveEditorToLastGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.moveEditorToLastGroup',
            title: ( localize2WithPath(_moduleId, 88, 'Move Editor into Last Group')),
            f1: true,
            keybinding: {
                weight: 200 ,
                primary: 1024  | 512  | 30 ,
                mac: {
                    primary: 2048  | 256  | 30
                }
            },
            category: Categories.View
        }, MOVE_ACTIVE_EDITOR_COMMAND_ID, { to: 'last', by: 'group' });
    }
}
class SplitEditorToPreviousGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorToPreviousGroup',
            title: ( localize2WithPath(_moduleId, 89, 'Split Editor into Previous Group')),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'previous', by: 'group' });
    }
}
class SplitEditorToNextGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorToNextGroup',
            title: ( localize2WithPath(_moduleId, 90, 'Split Editor into Next Group')),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'next', by: 'group' });
    }
}
class SplitEditorToAboveGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorToAboveGroup',
            title: ( localize2WithPath(_moduleId, 91, 'Split Editor into Group Above')),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'up', by: 'group' });
    }
}
class SplitEditorToBelowGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorToBelowGroup',
            title: ( localize2WithPath(_moduleId, 92, 'Split Editor into Group Below')),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'down', by: 'group' });
    }
}
class SplitEditorToLeftGroupAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.splitEditorToLeftGroup'; }
    static { this.LABEL = ( localizeWithPath(_moduleId, 93, "Split Editor into Left Group")); }
    constructor() {
        super({
            id: 'workbench.action.splitEditorToLeftGroup',
            title: ( localize2WithPath(_moduleId, 93, "Split Editor into Left Group")),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'left', by: 'group' });
    }
}
class SplitEditorToRightGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorToRightGroup',
            title: ( localize2WithPath(_moduleId, 94, 'Split Editor into Right Group')),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'right', by: 'group' });
    }
}
class SplitEditorToFirstGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorToFirstGroup',
            title: ( localize2WithPath(_moduleId, 95, 'Split Editor into First Group')),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'first', by: 'group' });
    }
}
class SplitEditorToLastGroupAction extends ExecuteCommandAction {
    constructor() {
        super({
            id: 'workbench.action.splitEditorToLastGroup',
            title: ( localize2WithPath(_moduleId, 96, 'Split Editor into Last Group')),
            f1: true,
            category: Categories.View
        }, COPY_ACTIVE_EDITOR_COMMAND_ID, { to: 'last', by: 'group' });
    }
}
class EditorLayoutSingleAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutSingle'; }
    constructor() {
        super({
            id: EditorLayoutSingleAction.ID,
            title: ( localize2WithPath(_moduleId, 97, 'Single Column Editor Layout')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{}] });
    }
}
class EditorLayoutTwoColumnsAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutTwoColumns'; }
    constructor() {
        super({
            id: EditorLayoutTwoColumnsAction.ID,
            title: ( localize2WithPath(_moduleId, 98, 'Two Columns Editor Layout')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{}, {}], orientation: 0  });
    }
}
class EditorLayoutThreeColumnsAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutThreeColumns'; }
    constructor() {
        super({
            id: EditorLayoutThreeColumnsAction.ID,
            title: ( localize2WithPath(_moduleId, 99, 'Three Columns Editor Layout')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{}, {}, {}], orientation: 0  });
    }
}
class EditorLayoutTwoRowsAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutTwoRows'; }
    constructor() {
        super({
            id: EditorLayoutTwoRowsAction.ID,
            title: ( localize2WithPath(_moduleId, 100, 'Two Rows Editor Layout')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{}, {}], orientation: 1  });
    }
}
class EditorLayoutThreeRowsAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutThreeRows'; }
    constructor() {
        super({
            id: EditorLayoutThreeRowsAction.ID,
            title: ( localize2WithPath(_moduleId, 101, 'Three Rows Editor Layout')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{}, {}, {}], orientation: 1  });
    }
}
class EditorLayoutTwoByTwoGridAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutTwoByTwoGrid'; }
    constructor() {
        super({
            id: EditorLayoutTwoByTwoGridAction.ID,
            title: ( localize2WithPath(_moduleId, 102, 'Grid Editor Layout (2x2)')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{ groups: [{}, {}] }, { groups: [{}, {}] }] });
    }
}
class EditorLayoutTwoColumnsBottomAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutTwoColumnsBottom'; }
    constructor() {
        super({
            id: EditorLayoutTwoColumnsBottomAction.ID,
            title: ( localize2WithPath(_moduleId, 103, 'Two Columns Bottom Editor Layout')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{}, { groups: [{}, {}] }], orientation: 1  });
    }
}
class EditorLayoutTwoRowsRightAction extends ExecuteCommandAction {
    static { this.ID = 'workbench.action.editorLayoutTwoRowsRight'; }
    constructor() {
        super({
            id: EditorLayoutTwoRowsRightAction.ID,
            title: ( localize2WithPath(_moduleId, 104, 'Two Rows Right Editor Layout')),
            f1: true,
            category: Categories.View
        }, LAYOUT_EDITOR_GROUPS_COMMAND_ID, { groups: [{}, { groups: [{}, {}] }], orientation: 0  });
    }
}
class AbstractCreateEditorGroupAction extends Action2 {
    constructor(desc, direction) {
        super(desc);
        this.direction = direction;
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const layoutService = accessor.get(IWorkbenchLayoutService);
        const activeDocument = getActiveDocument();
        const focusNewGroup = layoutService.hasFocus("workbench.parts.editor" ) || activeDocument.activeElement === activeDocument.body;
        const group = editorGroupService.addGroup(editorGroupService.activeGroup, this.direction);
        editorGroupService.activateGroup(group);
        if (focusNewGroup) {
            group.focus();
        }
    }
}
class NewEditorGroupLeftAction extends AbstractCreateEditorGroupAction {
    constructor() {
        super({
            id: 'workbench.action.newGroupLeft',
            title: ( localize2WithPath(_moduleId, 105, 'New Editor Group to the Left')),
            f1: true,
            category: Categories.View
        }, 2 );
    }
}
class NewEditorGroupRightAction extends AbstractCreateEditorGroupAction {
    constructor() {
        super({
            id: 'workbench.action.newGroupRight',
            title: ( localize2WithPath(_moduleId, 106, 'New Editor Group to the Right')),
            f1: true,
            category: Categories.View
        }, 3 );
    }
}
class NewEditorGroupAboveAction extends AbstractCreateEditorGroupAction {
    constructor() {
        super({
            id: 'workbench.action.newGroupAbove',
            title: ( localize2WithPath(_moduleId, 107, 'New Editor Group Above')),
            f1: true,
            category: Categories.View
        }, 0 );
    }
}
class NewEditorGroupBelowAction extends AbstractCreateEditorGroupAction {
    constructor() {
        super({
            id: 'workbench.action.newGroupBelow',
            title: ( localize2WithPath(_moduleId, 108, 'New Editor Group Below')),
            f1: true,
            category: Categories.View
        }, 1 );
    }
}
class ToggleEditorTypeAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.toggleEditorType',
            title: ( localize2WithPath(_moduleId, 109, 'Toggle Editor Type')),
            f1: true,
            category: Categories.View,
            precondition: ActiveEditorAvailableEditorIdsContext
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const editorResolverService = accessor.get(IEditorResolverService);
        const activeEditorPane = editorService.activeEditorPane;
        if (!activeEditorPane) {
            return;
        }
        const activeEditorResource = EditorResourceAccessor.getCanonicalUri(activeEditorPane.input);
        if (!activeEditorResource) {
            return;
        }
        const editorIds = ( (editorResolverService.getEditors(activeEditorResource).map(editor => editor.id))).filter(id => id !== activeEditorPane.input.editorId);
        if (editorIds.length === 0) {
            return;
        }
        await editorService.replaceEditors([
            {
                editor: activeEditorPane.input,
                replacement: {
                    resource: activeEditorResource,
                    options: {
                        override: editorIds[0]
                    }
                }
            }
        ], activeEditorPane.group);
    }
}
class ReOpenInTextEditorAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.reopenTextEditor',
            title: ( localize2WithPath(_moduleId, 110, 'Reopen Editor With Text Editor')),
            f1: true,
            category: Categories.View,
            precondition: ActiveEditorAvailableEditorIdsContext
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const activeEditorPane = editorService.activeEditorPane;
        if (!activeEditorPane) {
            return;
        }
        const activeEditorResource = EditorResourceAccessor.getCanonicalUri(activeEditorPane.input);
        if (!activeEditorResource) {
            return;
        }
        await editorService.replaceEditors([
            {
                editor: activeEditorPane.input,
                replacement: {
                    resource: activeEditorResource,
                    options: {
                        override: DEFAULT_EDITOR_ASSOCIATION.id
                    }
                }
            }
        ], activeEditorPane.group);
    }
}
class BaseMoveCopyEditorToNewWindowAction extends Action2 {
    constructor(id, title, keybinding, move) {
        super({
            id,
            title,
            category: Categories.View,
            precondition: ActiveEditorContext,
            keybinding,
            f1: true
        });
        this.move = move;
    }
    async run(accessor, resourceOrContext, context) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const { group, editor } = resolveCommandsContext(editorGroupService, getCommandsContext(resourceOrContext, context));
        if (group && editor) {
            const auxiliaryEditorPart = await editorGroupService.createAuxiliaryEditorPart();
            if (this.move) {
                group.moveEditor(editor, auxiliaryEditorPart.activeGroup);
            }
            else {
                group.copyEditor(editor, auxiliaryEditorPart.activeGroup);
            }
            auxiliaryEditorPart.activeGroup.focus();
        }
    }
}
class MoveEditorToNewWindowAction extends BaseMoveCopyEditorToNewWindowAction {
    constructor() {
        super(MOVE_EDITOR_INTO_NEW_WINDOW_COMMAND_ID, {
            ...( localize2WithPath(_moduleId, 111, "Move Editor into New Window")),
            mnemonicTitle: ( localizeWithPath(_moduleId, 112, "&&Move Editor into New Window")),
        }, undefined, true);
    }
}
class CopyEditorToNewindowAction extends BaseMoveCopyEditorToNewWindowAction {
    constructor() {
        super(COPY_EDITOR_INTO_NEW_WINDOW_COMMAND_ID, {
            ...( localize2WithPath(_moduleId, 113, "Copy Editor into New Window")),
            mnemonicTitle: ( localizeWithPath(_moduleId, 114, "&&Copy Editor into New Window")),
        }, { primary: KeyChord(2048  | 41 , 45 ), weight: 200  }, false);
    }
}
class BaseMoveCopyEditorGroupToNewWindowAction extends Action2 {
    constructor(id, title, move) {
        super({
            id,
            title,
            category: Categories.View,
            f1: true
        });
        this.move = move;
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const activeGroup = editorGroupService.activeGroup;
        const auxiliaryEditorPart = await editorGroupService.createAuxiliaryEditorPart();
        editorGroupService.mergeGroup(activeGroup, auxiliaryEditorPart.activeGroup, {
            mode: this.move ? 1  : 0
        });
        auxiliaryEditorPart.activeGroup.focus();
    }
}
class MoveEditorGroupToNewWindowAction extends BaseMoveCopyEditorGroupToNewWindowAction {
    constructor() {
        super(MOVE_EDITOR_GROUP_INTO_NEW_WINDOW_COMMAND_ID, {
            ...( localize2WithPath(_moduleId, 115, "Move Editor Group into New Window")),
            mnemonicTitle: ( localizeWithPath(_moduleId, 116, "&&Move Editor Group into New Window")),
        }, true);
    }
}
class CopyEditorGroupToNewWindowAction extends BaseMoveCopyEditorGroupToNewWindowAction {
    constructor() {
        super(COPY_EDITOR_GROUP_INTO_NEW_WINDOW_COMMAND_ID, {
            ...( localize2WithPath(_moduleId, 117, "Copy Editor Group into New Window")),
            mnemonicTitle: ( localizeWithPath(_moduleId, 118, "&&Copy Editor Group into New Window")),
        }, false);
    }
}
class RestoreEditorsToMainWindowAction extends Action2 {
    constructor() {
        super({
            id: 'workbench.action.restoreEditorsToMainWindow',
            title: {
                ...( localize2WithPath(_moduleId, 119, "Restore Editors into Main Window")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 120, "&&Restore Editors into Main Window")),
            },
            f1: true,
            precondition: IsAuxiliaryWindowFocusedContext,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        editorGroupService.mergeAllGroups(editorGroupService.mainPart.activeGroup);
    }
}
class NewEmptyEditorWindowAction extends Action2 {
    constructor() {
        super({
            id: NEW_EMPTY_EDITOR_WINDOW_COMMAND_ID,
            title: {
                ...( localize2WithPath(_moduleId, 121, "New Empty Editor Window")),
                mnemonicTitle: ( localizeWithPath(_moduleId, 122, "&&New Empty Editor Window")),
            },
            f1: true,
            category: Categories.View
        });
    }
    async run(accessor) {
        const editorGroupService = accessor.get(IEditorGroupsService);
        const auxiliaryEditorPart = await editorGroupService.createAuxiliaryEditorPart();
        auxiliaryEditorPart.activeGroup.focus();
    }
}
export { ClearEditorHistoryAction, ClearRecentFilesAction, CloseAllEditorGroupsAction, CloseAllEditorsAction, CloseEditorAction, CloseEditorInAllGroupsAction, CloseEditorsInOtherGroupsAction, CloseLeftEditorsInGroupAction, CloseOneEditorAction, CopyEditorGroupToNewWindowAction, CopyEditorToNewindowAction, DuplicateGroupDownAction, DuplicateGroupLeftAction, DuplicateGroupRightAction, DuplicateGroupUpAction, EditorLayoutSingleAction, EditorLayoutThreeColumnsAction, EditorLayoutThreeRowsAction, EditorLayoutTwoByTwoGridAction, EditorLayoutTwoColumnsAction, EditorLayoutTwoColumnsBottomAction, EditorLayoutTwoRowsAction, EditorLayoutTwoRowsRightAction, FocusAboveGroup, FocusActiveGroupAction, FocusBelowGroup, FocusFirstGroupAction, FocusLastGroupAction, FocusLeftGroup, FocusNextGroup, FocusPreviousGroup, FocusRightGroup, JoinAllGroupsAction, JoinTwoGroupsAction, MaximizeGroupHideSidebarAction, MinimizeOtherGroupsAction, MinimizeOtherGroupsHideSidebarAction, MoveEditorGroupToNewWindowAction, MoveEditorLeftInGroupAction, MoveEditorRightInGroupAction, MoveEditorToAboveGroupAction, MoveEditorToBelowGroupAction, MoveEditorToFirstGroupAction, MoveEditorToLastGroupAction, MoveEditorToLeftGroupAction, MoveEditorToNewWindowAction, MoveEditorToNextGroupAction, MoveEditorToPreviousGroupAction, MoveEditorToRightGroupAction, MoveGroupDownAction, MoveGroupLeftAction, MoveGroupRightAction, MoveGroupUpAction, NavigateBackwardsAction, NavigateBackwardsInEditsAction, NavigateBackwardsInNavigationsAction, NavigateBetweenGroupsAction, NavigateForwardAction, NavigateForwardInEditsAction, NavigateForwardInNavigationsAction, NavigatePreviousAction, NavigatePreviousInEditsAction, NavigatePreviousInNavigationsAction, NavigateToLastEditLocationAction, NavigateToLastNavigationLocationAction, NewEditorGroupAboveAction, NewEditorGroupBelowAction, NewEditorGroupLeftAction, NewEditorGroupRightAction, NewEmptyEditorWindowAction, OpenFirstEditorInGroup, OpenLastEditorInGroup, OpenNextEditor, OpenNextEditorInGroup, OpenNextRecentlyUsedEditorAction, OpenNextRecentlyUsedEditorInGroupAction, OpenPreviousEditor, OpenPreviousEditorInGroup, OpenPreviousRecentlyUsedEditorAction, OpenPreviousRecentlyUsedEditorInGroupAction, QuickAccessLeastRecentlyUsedEditorAction, QuickAccessLeastRecentlyUsedEditorInGroupAction, QuickAccessPreviousEditorFromHistoryAction, QuickAccessPreviousRecentlyUsedEditorAction, QuickAccessPreviousRecentlyUsedEditorInGroupAction, ReOpenInTextEditorAction, ReopenClosedEditorAction, ResetGroupSizesAction, RestoreEditorsToMainWindowAction, RevertAndCloseEditorAction, ShowAllEditorsByAppearanceAction, ShowAllEditorsByMostRecentlyUsedAction, ShowEditorsInActiveGroupByMostRecentlyUsedAction, SplitEditorAction, SplitEditorDownAction, SplitEditorLeftAction, SplitEditorOrthogonalAction, SplitEditorRightAction, SplitEditorToAboveGroupAction, SplitEditorToBelowGroupAction, SplitEditorToFirstGroupAction, SplitEditorToLastGroupAction, SplitEditorToLeftGroupAction, SplitEditorToNextGroupAction, SplitEditorToPreviousGroupAction, SplitEditorToRightGroupAction, SplitEditorUpAction, ToggleEditorTypeAction, ToggleGroupSizesAction, ToggleMaximizeEditorGroupAction, UnpinEditorAction };
